import { AlertChunky } from '@eventbrite/eds-iconography';
import { TYPE_ERROR } from '@eventbrite/eds-notification';
import { withMainControls } from '@eventbrite/eds-structure';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils'; /* eslint-disable react/jsx-handler-names */
import { cleanAction, getAction } from '@eventbrite/user-action-cookie';
import loadable from '@loadable/component';
import classnames from 'classnames';
import queryString from 'querystring';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import {
    GA_FOLLOWED_ORGANIZERS_FILTER,
    GA_SEARCH_CATEGORY,
} from '../../../../constants/analytics';
import { trackPageView as trackPageViewAction } from '../../../../redux/actions/analytics';
import { setSearchInformationFromUrl as setSearchInformationFromUrlAction } from '../../../../redux/actions/location';
import { updateSearchInformationFromUrl as updateSearchInformationFromUrlAction } from '../../../../redux/actions/search';
import { LazyMapPanelContainer } from '../MapPanelContainer';
import { SearchFooterContainer } from '../SearchFooterContainer';
import { SearchPivotsContainer } from '../SearchPivotsContainer';
import { SearchResultsPanelContainer } from '../SearchResultsPanelContainer';
import './SearchBaseContainer.scss';

const FilterPanelContainer = loadable(() => import('../FilterPanelContainer'), {
    resolveComponent: (component: { FilterPanelContainer: React.ReactNode }) =>
        component.FilterPanelContainer,
});

interface SearchBaseScreenProps {
    /**
     * GA Utility for tracking the pageview of search
     */
    onTrackPageViewAction: Function;
    /**
     * Sets current search information state from the URL
     */
    setSearchInformationFromUrl: Function;
    /**
     * Gets current search information state from the URL
     */
    updateSearchInformationFromUrl: Function;
    /**
     * History object from React Router
     */
    history: RouteComponentProps['history'];
    location: RouteComponentProps['location'];
    /**
     * Type of individual filter to show in the filter panel
     * or '' if all filters must be shown
     */
    filterTypeToShow?: $FixMe;
    isAuthenticated?: boolean;
    eventCount?: number;
    showErrorNotification?: boolean;
    addMainNotification?: Function;
    hideMainNotification?: Function;
    isAnimatedDrawerOpen?: boolean;
    isOnline: boolean;
    isBotRequest: boolean;
}

export class SearchBaseScreen extends React.PureComponent<
    SearchBaseScreenProps
> {
    componentDidMount() {
        const {
            isAuthenticated,
            onTrackPageViewAction,
            setSearchInformationFromUrl,
            eventCount,
        } = this.props;

        // set the pair location-searchCriterias so we can use them later when
        // the user try to navigate back
        setSearchInformationFromUrl(
            this.props.history.location.pathname,
            queryString.parse(this.props.history.location.search),
        );

        // track pageview GA action
        onTrackPageViewAction();

        if (isAuthenticated) {
            const action = getAction();

            if (action && action.action === 'FollowedOrganizerFilterAttempt') {
                cleanAction();

                trackAnalyticsEvent({
                    category: GA_SEARCH_CATEGORY,
                    action: GA_FOLLOWED_ORGANIZERS_FILTER,
                    label: eventCount,
                });
            }
        }
    }

    componentDidUpdate(prevProps: SearchBaseScreenProps) {
        if (
            prevProps.location.pathname + prevProps.location.search !==
            this.props.location.pathname + this.props.location.search
        ) {
            // TODO: - MD: Appears to be the source of bug for EB-131855
            // TRIED: encodeURIComponent on pathname, but it is already PARTIALLY encoded % was not encoded, but $ was encoded to %26
            // This is for supporting the back button. We can remove
            // this if we refactor the app to derive the page state
            // directly from the URL
            this.props.updateSearchInformationFromUrl(
                this.props.location.pathname,
                queryString.parse(this.props.location.search.replace('?', '')),
            );
        }

        //Handle rendering errors related to search or page changes
        if (
            !!this.props.showErrorNotification &&
            !prevProps.showErrorNotification
        ) {
            this.props.addMainNotification?.({
                type: TYPE_ERROR,
                children: this.props.showErrorNotification,
                iconType: <AlertChunky />,
                hasCloseButton: true,
            });
        }

        if (
            !this.props.showErrorNotification &&
            !!prevProps.showErrorNotification
        ) {
            this.props.hideMainNotification?.();
        }
    }

    render() {
        const { isAnimatedDrawerOpen, isOnline, isBotRequest } = this.props;

        //TODO: Only import FilterPanelContainer if mobile screen
        return (
            <div
                className={classnames('search-base-screen__root', {
                    'search-base-screen--is-mobile-filter-open': isAnimatedDrawerOpen,
                    'search-base-screen--bot-request': isBotRequest,
                })}
            >
                <FilterPanelContainer />
                <section className="search-base-screen__search-panel">
                    <SearchResultsPanelContainer history={this.props.history} />
                    <SearchFooterContainer />
                    <SearchPivotsContainer />
                </section>

                <section className="search-base-screen__map-panel">
                    <LazyMapPanelContainer isOnline={isOnline} />
                </section>
            </div>
        );
    }
}

const _mapStateToProps = (state: {
    ui: { isAnimatedDrawerOpen?: boolean; showErrorNotification?: boolean };
    pagination: any;
    auth: { isAuthenticated?: boolean };
    location: { isOnline?: boolean };
    app: { isBotRequest?: boolean };
}) => ({
    isAnimatedDrawerOpen: state.ui.isAnimatedDrawerOpen,
    showErrorNotification: state.ui.showErrorNotification,
    eventCount:
        state.pagination.event[Object.keys(state.pagination.event)[0]]
            ?.objectCount,
    //Props below for withEventSaveNotification HoC
    isAuthenticated: state.auth.isAuthenticated,
    isOnline: state.location.isOnline,
    isBotRequest: state.app.isBotRequest,
});

const _mapDispatchToProps = (
    dispatch: Function,
    _ownProps: SearchBaseScreenProps,
) => ({
    setSearchInformationFromUrl: (...args: [$FixMe]) =>
        dispatch(setSearchInformationFromUrlAction(...args)),
    updateSearchInformationFromUrl: (...args: [$FixMe]) =>
        dispatch(updateSearchInformationFromUrlAction(...args)),
    onTrackPageViewAction: (...args: [$FixMe]) =>
        dispatch(trackPageViewAction(...args)),
});

const enhance = compose(
    withMainControls,
    withRouter,
    connect(_mapStateToProps, _mapDispatchToProps),
);

export const EnhancedSearchBaseScreen = enhance(SearchBaseScreen);
